import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { CricketHeader } from '#/components/layout/CricketHeader'
import { Switch } from '#/components/ui/switch'
import { useCricketStore } from '#/store/cricketStore'
import { THEMES, applyTheme, type ThemeId } from '#/lib/themes'
export const Route = createFileRoute('/settings')({
  component: MatchSettingsScreen,
})

function MatchSettingsScreen() {
  const navigate = useNavigate()
  const settings = useCricketStore((state) => state.matchSettings)
  const updateSettings = useCricketStore((state) => state.updateSettings)

  const [playersPerTeam, setPlayersPerTeam] = useState(settings.playersPerTeam)
  const [singlePlayerMode, setSinglePlayerMode] = useState(settings.singlePlayerMode ?? false)
  const [noBallEnabled, setNoBallEnabled] = useState(settings.noBallEnabled)
  const [noBallReball, setNoBallReball] = useState(settings.noBallReball)
  const [noBallRun, setNoBallRun] = useState(settings.noBallRun)

  const [wideBallEnabled, setWideBallEnabled] = useState(settings.wideBallEnabled)
  const [wideBallReball, setWideBallReball] = useState(settings.wideBallReball)
  const [wideBallRun, setWideBallRun] = useState(settings.wideBallRun)

  const [currentTheme, setCurrentTheme] = useState<ThemeId>('classic-green')

  useEffect(() => {
    try {
      const saved = localStorage.getItem('cric_theme') as ThemeId
      if (saved && THEMES[saved]) {
        setCurrentTheme(saved)
      }
    } catch {
      // fallback
    }
  }, [])

  const handleSelectTheme = (themeId: ThemeId) => {
    setCurrentTheme(themeId)
    applyTheme(themeId)
  }

  const handleSave = () => {
    updateSettings({
      playersPerTeam: Number(playersPerTeam) || 11,
      singlePlayerMode: singlePlayerMode || Number(playersPerTeam) === 1,
      noBallEnabled,
      noBallReball,
      noBallRun: Number(noBallRun) || 1,
      wideBallEnabled,
      wideBallReball,
      wideBallRun: Number(wideBallRun) || 1,
    })
    navigate({ to: '/' })
  }

  return (
    <div className="flex-1 flex flex-col justify-between min-h-screen">
      <div>
        <CricketHeader
          title="Match Settings"
          showActions={false}
          showBack={true}
          onBack={() => navigate({ to: '/' })}
        />

        <div className="p-4 space-y-6">
          {/* Players per team */}
          <div>
            <label className="text-sm font-semibold theme-text-primary block mb-2">
              Players per team?
            </label>
            <div className="theme-card rounded-lg p-3.5 shadow-sm">
              <input
                type="number"
                min="1"
                max="25"
                value={playersPerTeam}
                onChange={(e) => {
                  const val = Number(e.target.value)
                  setPlayersPerTeam(val)
                  if (val === 1) setSinglePlayerMode(true)
                }}
                className="w-full border-b border-[var(--theme-border)] py-1 px-0 bg-transparent text-[var(--theme-text)] focus:outline-none focus:border-[var(--color-cricket-primary)] text-base"
              />
            </div>
          </div>

          {/* Single Player Match Mode */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-semibold theme-text-primary block">
                  Single Player Mode (No Non-Striker)
                </span>
                <span className="text-xs opacity-70">
                  Only 1 batsman bats and runs alone (no non-striker required)
                </span>
              </div>
              <Switch
                checked={singlePlayerMode || Number(playersPerTeam) === 1}
                onCheckedChange={setSinglePlayerMode}
                disabled={Number(playersPerTeam) === 1}
              />
            </div>
          </div>

          {/* No Ball */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold theme-text-primary">
                No Ball
              </span>
              <Switch
                checked={noBallEnabled}
                onCheckedChange={setNoBallEnabled}
              />
            </div>

            {noBallEnabled && (
              <div className="theme-card rounded-lg p-4 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-base text-[var(--theme-text)]">Re-ball</span>
                  <Switch
                    checked={noBallReball}
                    onCheckedChange={setNoBallReball}
                  />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-base text-[var(--theme-text)]">No ball run</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={noBallRun}
                    onChange={(e) => setNoBallRun(Number(e.target.value))}
                    className="w-16 border-b border-[var(--theme-border)] bg-transparent text-center py-1 text-base focus:outline-none focus:border-[var(--color-cricket-primary)] text-[var(--theme-text)]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Wide Ball */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold theme-text-primary">
                Wide Ball
              </span>
              <Switch
                checked={wideBallEnabled}
                onCheckedChange={setWideBallEnabled}
              />
            </div>

            {wideBallEnabled && (
              <div className="theme-card rounded-lg p-4 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-base text-[var(--theme-text)]">Re-ball</span>
                  <Switch
                    checked={wideBallReball}
                    onCheckedChange={setWideBallReball}
                  />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-base text-[var(--theme-text)]">Wide ball run</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={wideBallRun}
                    onChange={(e) => setWideBallRun(Number(e.target.value))}
                    className="w-16 border-b border-[var(--theme-border)] bg-transparent text-center py-1 text-base focus:outline-none focus:border-[var(--color-cricket-primary)] text-[var(--theme-text)]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Theme & Color Customization Section */}
          <div className="space-y-3 pt-2 border-t border-[var(--theme-border,#e5e7eb)]">
            <div>
              <span className="text-sm font-semibold text-[var(--color-cricket-primary,#2E7D32)] block">
                App Themes & Colors
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Customize look, pitch colors, and night dark mode
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2.5 pt-1">
              {Object.values(THEMES).map((th) => {
                const isSelected = currentTheme === th.id
                return (
                  <button
                    key={th.id}
                    type="button"
                    onClick={() => handleSelectTheme(th.id)}
                    className={`p-3 rounded-xl border text-left flex flex-col justify-between transition-all select-none cursor-pointer ${
                      isSelected
                        ? 'border-[var(--color-cricket-primary)] ring-2 ring-[var(--color-cricket-primary)]/30 bg-[var(--theme-card-bg)] shadow-sm'
                        : 'border-[var(--theme-border)] bg-[var(--theme-card-bg)] hover:border-[var(--color-cricket-primary)] hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full mb-2">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="size-4 rounded-full shadow-xs inline-block"
                          style={{ backgroundColor: th.primary }}
                        />
                        <span
                          className="size-3 rounded-full opacity-70 inline-block"
                          style={{ backgroundColor: th.dark }}
                        />
                      </div>
                      {isSelected && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--color-cricket-primary,#2E7D32)] text-white">
                          Active
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-800 dark:text-gray-100 leading-tight">
                        {th.name}
                      </div>
                      <div className="text-[10px] text-gray-400 dark:text-gray-400 truncate mt-0.5">
                        {th.description}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Save settings fixed at bottom */}
      <div className="p-4 mt-auto">
        <button
          onClick={handleSave}
          className="w-full bg-[var(--color-cricket-primary,#2E7D32)] text-white font-semibold py-3.5 rounded-lg shadow-md hover:opacity-90 active:scale-98 transition-all text-base"
        >
          Save settings
        </button>
      </div>
    </div>
  )
}
